import React, { useState } from 'react';
import { supabase } from './lib/supabase';
import { Navigation } from './components/Navigation';
import { MapView } from './components/MapView';
import { ActivitiesView } from './components/ActivitiesView';

function App() {
  const [activeView, setActiveView] = useState<'map' | 'activities'>('map');
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [totalActivities, setTotalActivities] = useState<number>(0);
  const [filteredActivities, setFilteredActivities] = useState<number>(0);

  // Verificar configuración al cargar
  React.useEffect(() => {
    const checkConfig = () => {
      if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
        setError('Configuración de Supabase faltante');
        return;
      }
      
      // Test de conexión a Supabase
      supabase.from('mapa_grupos').select('count').limit(1)
        .then(({ error }) => {
          if (error) {
            console.error('Supabase connection error:', error);
            setError('Error de conexión a la base de datos');
          }
        })
        .catch((err) => {
          console.error('Network error:', err);
          setError('Error de red');
        });
    };

    checkConfig();
  }, []);

  // Cargar años disponibles y estadísticas
  React.useEffect(() => {
    const loadStats = async () => {
      try {
        const { data, error } = await supabase
          .from('mapa_actividades')
          .select('fecha');

        if (error) {
          console.error('Error loading stats:', error);
        } else {
          const activities = data || [];
          setTotalActivities(activities.length);
          
          // Extraer años únicos
          const years = [...new Set(activities.map(actividad => 
            new Date(actividad.fecha).getFullYear().toString()
          ))].sort((a, b) => b.localeCompare(a));
          setAvailableYears(years);
          
          // Calcular actividades filtradas
          if (selectedYear) {
            const filtered = activities.filter(actividad => 
              new Date(actividad.fecha).getFullYear().toString() === selectedYear
            );
            setFilteredActivities(filtered.length);
          } else {
            setFilteredActivities(activities.length);
          }
        }
      } catch (error) {
        console.error('Network error loading stats:', error);
      }
    };

    loadStats();
  }, [selectedYear]);
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error de Configuración</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Verifica que las variables de entorno estén configuradas correctamente.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      <Navigation 
        activeView={activeView} 
        onViewChange={setActiveView}
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        availableYears={availableYears}
        totalActivities={totalActivities}
        filteredActivities={filteredActivities}
      />
      
      <main className="flex-1 relative overflow-hidden main-content">
        {activeView === 'map' ? (
          <MapView 
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            availableYears={availableYears}
          />
        ) : (
          <ActivitiesView 
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            availableYears={availableYears}
          />
        )}
      </main>
    </div>
  );
}

export default App;